import { releaseToolLockSnapshot } from '@/api/toolConfig.api';
import { log } from '@/logger/logger';
import { useWorkflowStore } from '@/state/workflow.state';
import { sanitizeError } from '@/utils/sanitize-error';
import { applyCoreToolConfigChange, queueToolConfigWrite } from './toolConfigWriteQueue';
import type { ToolConfig } from '@/types/tool-config.types';
import type { NodeId } from 'web';

const logger = log.getLogger('toolLockSnapshot');

/** Unlocks waiting to be sent, per workflow, so a multi-selection unlock is one request. */
const pending = new Map<string, Set<NodeId>>();

/** The release route's `@ArrayMaxSize`. */
const MAX_RELEASE_BATCH = 200;

function withoutReleased(config: ToolConfig | null, nodeId: NodeId): ToolConfig | null {
  const snapshot = config?.lockedAtConversion;
  if (!config || !snapshot?.includes(nodeId)) {
    return config;
  }
  return { ...config, lockedAtConversion: snapshot.filter((id) => id !== nodeId) };
}

/**
 * Nodes unlocked while the snapshot is being taken, per workflow. Core takes it from the saved graph,
 * which can still show them locked, so they are released once it lands.
 */
const unlockedDuringTake = new Map<string, Set<NodeId>>();

/** Starts recording unlocks for a snapshot about to be taken. */
export function openToolLockSnapshotTake(recipeId: string): void {
  unlockedDuringTake.set(recipeId, new Set());
}

/** Stops recording, and releases what was unlocked meanwhile from the snapshot that landed, if one did. */
export function closeToolLockSnapshotTake(recipeId: string, snapshot: readonly string[] | null): void {
  const unlocked = unlockedDuringTake.get(recipeId);
  unlockedDuringTake.delete(recipeId);
  for (const nodeId of unlocked ?? []) {
    if (snapshot?.includes(nodeId)) {
      releaseFromToolLockSnapshot(recipeId, nodeId);
    }
  }
}

/**
 * Takes a node out of the lock snapshot for good: it was unlocked, or the builder exposed it. The store
 * drops it at once, along with any config write still in flight, so that write's success, next edit or
 * rollback can't bring it back. Core hears on the next microtask, queued behind those writes.
 */
export function releaseFromToolLockSnapshot(recipeId: string, nodeId: NodeId): void {
  applyCoreToolConfigChange(recipeId, (config) => withoutReleased(config, nodeId));

  const batch = pending.get(recipeId);
  if (batch) {
    batch.add(nodeId);
    return;
  }
  pending.set(recipeId, new Set([nodeId]));
  queueMicrotask(() => {
    const nodeIds = [...(pending.get(recipeId) ?? [])];
    pending.delete(recipeId);
    for (let start = 0; start < nodeIds.length; start += MAX_RELEASE_BATCH) {
      const chunk = nodeIds.slice(start, start + MAX_RELEASE_BATCH);
      queueToolConfigWrite(() => releaseToolLockSnapshot(recipeId, chunk)).catch((error: unknown) =>
        logger.warn('Tool lock snapshot release failed', sanitizeError(error)),
      );
    }
  });
}

/**
 * Called when a node is unlocked. A node in the lock snapshot was hidden by its lock; once unlocked it
 * leaves the snapshot for good, so locking it again later no longer hides it: once the snapshot is taken
 * a lock decides nothing.
 */
export function releaseUnlockedNodeFromToolLockSnapshot(nodeId: NodeId): void {
  const { recipe, workflowRole } = useWorkflowStore.getState();
  if (workflowRole !== 'editor') {
    return;
  }
  if (!Array.isArray(recipe.toolConfig?.lockedAtConversion)) {
    unlockedDuringTake.get(recipe.id)?.add(nodeId);
    return;
  }
  if (recipe.toolConfig.lockedAtConversion.includes(nodeId)) {
    releaseFromToolLockSnapshot(recipe.id, nodeId);
  }
}
